// Inspired from https://github.com/juyeong1260/quill-auto-detect-url/blob/main/src/index.ts
// Translated from TypeScript to JavaScript with https://transform.tools/typescript-to-javascript

import * as linkify from "linkifyjs"
import Quill from "quill"
import Delta from "quill-delta"

class LinkConverter {
  constructor(quill, options) {
    this.quill = quill
    options = options || {}

    this.options = options
    this.registerPasteListener()
    this.registerTypeListener()
  }
  registerPasteListener() {
    this.quill.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) => {
      // Pasting results in a sequence of calls to this function,
      // with delta = { ops: [ { insert: "some text" } ] }
      //
      // For some odd reason, when pasting from Word Desktop, `node.data`
      // has "some text" but with some extra line breaks, so we rely on `delta`.
      if (delta.ops.length !== 1 || !delta.ops[0].insert) return delta

      return this.textToDelta(delta.ops[0].insert)
    })
  }
  registerTypeListener() {
    this.quill.on("text-change", (delta) => {
      const ops = delta.ops
      if (!ops || ops.length < 1 || ops.length > 2) {
        return
      }
      const lastOp = ops[ops.length - 1]
      if (
        !lastOp.delete &&
        (!lastOp.insert || typeof lastOp.insert !== "string")
      ) {
        return
      }
      requestAnimationFrame(() => {
        this.checkTextForUrl(lastOp.insert)
      })
    })
  }

  checkTextForUrl(insert) {
    // This runs in the next animation frame; focus may have left the editor.
    // Reading the selection must not pull it back from the user's next control.
    const sel = this.quill.getSelection()
    if (!sel) {
      return
    }
    const [currentLeaf] = this.quill.getLeaf(sel.index)
    if (!currentLeaf) {
      return
    }

    if (insert === undefined) {
      const [nextLeaf] = this.quill.getLeaf(sel.index + 1)
      if (
        nextLeaf &&
        nextLeaf !== currentLeaf &&
        currentLeaf?.parent.domNode.localName === "a"
      ) {
        this.transformLeafBeforeLink(currentLeaf, nextLeaf)
      } else {
        this.transformLeaf(currentLeaf)
      }
    } else if (insert === "\n") {
      const [prevLeaf] = this.quill.getLeaf(sel.index - 1)
      if (prevLeaf && prevLeaf.parent.domNode.localName === "a") {
        this.transformLeaf(prevLeaf)
      }
      this.transformLeaf(currentLeaf)
    } else {
      const [prevLeaf] = this.quill.getLeaf(sel.index - 1)

      if (
        prevLeaf?.parent.domNode.localName === "a" &&
        prevLeaf.parent.domNode !== currentLeaf?.parent.domNode
      ) {
        this.transformLeafBeforeLink(prevLeaf, currentLeaf)
      } else {
        this.transformLeaf(currentLeaf)
      }
    }
  }
  transformLeafBeforeLink(currentLeaf, nextLeaf) {
    const url =
      currentLeaf.domNode.textContent + (nextLeaf.domNode.textContent || "")

    const [match] = linkify.find(url)
    if (match) {
      const quillIndex = this.quill.getIndex(currentLeaf) + match.start
      this.quill.formatText(
        quillIndex,
        currentLeaf.domNode.textContent?.length ?? 0,
        "link",
        false,
      )
      this.textToUrl(quillIndex, match.value, match.href)
    }
  }
  transformLeaf(leaf) {
    const value = leaf.value() ?? ""
    if (typeof value !== "string") {
      // E.g. a leaf that is a htmlQuote
      return
    }
    const [match] = linkify.find(value)

    const leafIndex = this.quill.getIndex(leaf)
    if (match) {
      this.textToUrl(leafIndex + match.start, match.value, match.href)
    } else if (leaf.parent.domNode.localName === "a") {
      // When editing a link and the title is no longer a link, keep as a link
      // and do nothing
    }
  }
  textToUrl(index, value, href) {
    const ops = new Delta().retain(index).retain(value.length, { link: href })

    this.quill.updateContents(ops)
  }

  textToDelta(text) {
    const delta = new Delta()
    const links = linkify.find(text)
    links.push({ start: text.length })
    let lastIndex = 0
    for (const match of links) {
      const head = text.substring(lastIndex, match.start)
      delta.insert(head)
      if (match.href) {
        const url = text.substring(match.start, match.end)
        delta.insert(url, { link: match.href })
        lastIndex = match.end
      }
    }
    return delta
  }
}

Quill.register("modules/linkConverter", LinkConverter)
